import {
  HttpInterceptorFn,
  HttpResponse,
  provideHttpClient,
  withInterceptors,
  withRequestsMadeViaParent,
} from '@angular/common/http';
import { EnvironmentProviders } from '@angular/core';
import { delay, of } from 'rxjs';

import {
  TrendImportDayStatus,
  TrendImportItem,
  TrendImportMonitorModel,
  TrendImportStoreStatus,
} from './trend-import-monitor.model';
import { TREND_IMPORT_MONITOR_URL } from './trend-import-monitor.service';

/**
 * Mock data for the trend import monitor until `GET /api/v1/sys/monitor/trend-imports` exists in the backend.
 * Remove `provideTrendImportMonitorMock()` from the route in `dmb-ui.routing.ts` to use the real endpoint.
 */

export const TREND_IMPORT_MONITOR_MOCK_DAYS = 34;

type DayValue = Omit<TrendImportDayStatus, 'date' | 'imports'>;
type Pattern = (day: number, random: () => number) => DayValue;

const complete: DayValue = { progress: 1, comment: null };
const notYetAvailable: DayValue = { progress: 0, comment: 'Profile#1: Report not yet available' };

/** Deterministic pseudo random numbers, so the mock looks the same on every reload */
function seededRandom(seed: number): () => number {
  let a = seed;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Reports arrive `lag` days late, otherwise complete */
const lagging =
  (lag: number, overrides: Record<number, DayValue> = {}): Pattern =>
  (day) =>
    overrides[day] ?? (day < lag ? notYetAvailable : complete);

/** Only some of the profiles deliver files */
const partialProfiles =
  (loaded: number, total: number): Pattern =>
  (day) =>
    day < 2
      ? notYetAvailable
      : { progress: loaded / total, comment: `Profile#7 [social]: ${loaded} of ${total} files loaded` };

/** Store delivers irregularly */
const irregular =
  (lag: number, gapRate: number): Pattern =>
  (day, random) =>
    day < lag || random() < gapRate ? { progress: 0, comment: 'Profile#3: No file delivered' } : complete;

const noImportsExpected: Pattern = () => ({ progress: 0, comment: 'No imports expected' });

const unknown = (comment = 'Unknown status'): DayValue => ({ progress: -1, comment });
const partial = (progress: number, comment: string): DayValue => ({ progress, comment });

const stores: [id: number, name: string, pattern: Pattern][] = [
  [
    1,
    'Amazon',
    lagging(2, {
      1: partial(0.05, 'Profile#11 [music-central]: 1 of 20 files loaded'),
      3: partial(0.05, 'Profile#11 [music-central]: 1 of 20 files loaded'),
      4: notYetAvailable,
      5: notYetAvailable,
      6: notYetAvailable,
      7: partial(0.6, 'Profile#11 [music-central]: 12 of 20 files loaded'),
    }),
  ],
  [2, 'Amazon Instant Video', lagging(3, { 0: partial(0.7, 'Profile#12: 7 of 10 files loaded'), 1: unknown() })],
  [3, 'Apple Books', lagging(2, { 2: partial(0.4, 'Profile#21: 2 of 5 files loaded'), 10: unknown() })],
  [
    4,
    'Apple Music / iTunes',
    (day) =>
      day < 2
        ? notYetAvailable
        : day < 12 || day > 30
          ? partial(0.75, 'Profile#22 [itunes]: 3 of 4 files loaded')
          : complete,
  ],
  [5, 'Beatport', lagging(2)],
  [6, 'buecher.de (Audio)', lagging(8)],
  [7, 'Ceebo (Audio)', irregular(3, 0.2)],
  [8, 'Deezer', lagging(2, { 3: notYetAvailable, 4: notYetAvailable })],
  [9, 'eBook.de (Audio)', lagging(7, { 4: complete })],
  [10, 'Facebook', partialProfiles(2, 5)],
  [11, 'GfK', lagging(2, { 0: partial(0.75, 'Profile#31: 3 of 4 files loaded'), 1: notYetAvailable, 32: unknown() })],
  [12, 'Hugendubel (Audio)', lagging(2, { 28: notYetAvailable, 29: notYetAvailable })],
  [13, 'Instagram', partialProfiles(2, 5)],
  [14, 'Libri (Audio)', lagging(7)],
  [15, 'Orell Fuessli (Audio)', lagging(2, { 29: notYetAvailable })],
  [16, 'Osiander (Audio)', lagging(24)],
  [17, 'Pandora', lagging(2, { 8: partial(0.3, 'Profile#41: 3 of 10 files loaded') })],
  [18, 'Peloton', lagging(2)],
  [19, 'Qobuz', lagging(1)],
  [20, 'Shazam', (day, random) => (day < 8 && day >= 2 && day !== 5 ? complete : noImportsExpected(day, random))],
  [21, 'Spotify', lagging(7, { 8: partial(0.5, 'Profile#51 [spotify]: 1 of 2 files loaded') })],
  [22, 'Spotify Audiobooks', lagging(2)],
  [23, 'TDC (24-7)', lagging(1, { 4: notYetAvailable })],
  [24, 'Thalia (Audio)', lagging(2)],
  [25, 'TikTok', lagging(2, { 4: notYetAvailable, 5: notYetAvailable, 6: notYetAvailable })],
  [26, 'Traxsource', lagging(4)],
  [27, 'Weltbild (Audio)', noImportsExpected],
  [
    28,
    'WhatsApp',
    lagging(2, {
      0: partial(0.4, 'Profile#61: 2 of 5 files loaded'),
      1: notYetAvailable,
      2: partial(0.4, 'Profile#61: 2 of 5 files loaded'),
    }),
  ],
];

function slug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

/** Creates the single imports behind a day, consistent with the day's progress */
function createItems(
  storeId: number,
  storeName: string,
  date: string,
  day: number,
  value: DayValue,
): TrendImportItem[] {
  if (value.comment === 'No imports expected') {
    return [];
  }
  const random = seededRandom(storeId * 1000 + day);
  const files = value.comment?.match(/(\d+) of (\d+) files/);
  const total = files ? Number(files[2]) : 1 + (storeId % 3);
  const loaded = files
    ? Number(files[1])
    : value.progress >= 1
      ? total
      : value.progress > 0
        ? Math.max(1, Math.min(total - 1, Math.round(value.progress * total)))
        : 0;

  return Array.from({ length: total }, (_, i): TrendImportItem => {
    const profileId = storeId * 10 + (i % 2) + 1;
    const account = `${slug(storeName)}-${(i % 2) + 1}`;
    const importId = 4_000_000 + storeId * 1000 + day * 10 + i;
    const totalRows = 1_000 + Math.floor(random() * 90_000);
    const base = {
      profile_id: profileId,
      account,
      file_name: `${slug(storeName)}_${account}_${date}_${i + 1}.tsv.gz`,
      total_rows: totalRows,
    };

    if (value.progress < 0 && i === 0) {
      return {
        ...base,
        import_id: importId,
        status: 'failed',
        stage: 'transform',
        progress: -1,
        written_rows: Math.floor(totalRows * 0.2),
        comment: 'Import job did not report back, status unknown',
      };
    }
    if (i < loaded || value.progress < 0) {
      return {
        ...base,
        import_id: importId,
        status: 'finished',
        stage: 'load',
        progress: 1,
        written_rows: totalRows,
        comment: null,
      };
    }
    if (value.progress > 0 && i === loaded) {
      return {
        ...base,
        import_id: importId,
        status: 'running',
        stage: 'transform',
        progress: 0.4,
        written_rows: Math.floor(totalRows * 0.4),
        comment: null,
      };
    }
    if (value.progress > 0 && i === loaded + 1) {
      return {
        ...base,
        import_id: importId,
        status: 'scheduled',
        stage: 'extract',
        progress: 0,
        written_rows: 0,
        comment: null,
      };
    }
    return {
      ...base,
      import_id: null,
      file_name: null,
      total_rows: null,
      status: 'missing',
      stage: null,
      progress: 0,
      written_rows: null,
      comment: 'Report not yet delivered by the store',
    };
  });
}

function formatDay(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export function createTrendImportMonitorMock(start?: string, now = new Date()): TrendImportMonitorModel {
  const startDate = start
    ? new Date(`${start}T00:00:00Z`)
    : new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
  const dates = Array.from({ length: TREND_IMPORT_MONITOR_MOCK_DAYS }, (_, i) => {
    const d = new Date(startDate);
    d.setUTCDate(d.getUTCDate() - i);
    return formatDay(d);
  });

  return {
    generated_at: now.toISOString(),
    stores: stores.map(([storeId, name, pattern]): TrendImportStoreStatus => {
      const random = seededRandom(storeId);
      return {
        store_id: storeId,
        store_name: name,
        imported_by_kotlin: true,
        days: dates.map((date, day) => {
          const value = pattern(day, random);
          return {
            date,
            ...value,
            comment: value.comment ?? null,
            imports: createItems(storeId, name, date, day, value),
          };
        }),
      };
    }),
  };
}

/** Answers trend import monitor requests with {@link createTrendImportMonitorMock} */
export const trendImportMonitorMockInterceptor: HttpInterceptorFn = (req, next) => {
  if (!req.url.startsWith(TREND_IMPORT_MONITOR_URL)) {
    return next(req);
  }
  const body = createTrendImportMonitorMock(req.params.get('start') ?? undefined);
  return of(new HttpResponse({ status: 200, url: req.url, body })).pipe(delay(300));
};

/**
 * Route level HttpClient that serves the mock and passes every other request to the app's HttpClient
 * (so the global interceptors still apply).
 */
export function provideTrendImportMonitorMock(): EnvironmentProviders {
  return provideHttpClient(withInterceptors([trendImportMonitorMockInterceptor]), withRequestsMadeViaParent());
}
